<template>
  <div class="goal-change-list">
    <div
      v-if="loading && goalChanges.length === 0"
      class="_spinner"
    >
      <m-spinner size="sm" />
    </div>
    <div
      v-else-if="failed && goalChanges.length === 0"
      class="_hint"
    >
      {{ $t('error.default') }}
    </div>
    <div
      v-else-if="isEmpty"
      class="_hint"
    >
      {{ emptyText }}
    </div>
    <template v-else>
      <div
        v-for="group in groups"
        :key="group.changes[0].uid"
        class="_group"
      >
        <user-avatar
          :user="actor(group)"
          :size="20"
          class="_avatar"
        />
        <div class="_content">
          <div class="_headline">
            <span class="_name">{{ actorName(group) }}</span> {{ summary(group) }}
          </div>
          <div class="_time">
            {{ timeAgo(group.to) }}
          </div>
          <div class="_changes">
            <goal-change-item
              v-for="item in group.items"
              :key="item[0].uid"
              :changes="item"
              :goal="goal"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!hasMore && createdInWindow"
        class="_group"
      >
        <user-avatar
          :user="creator"
          :size="20"
          class="_avatar"
        />
        <div class="_content">
          <div class="_headline">
            <span class="_name">{{ displayName(creator, $t('goalChanges.unknownUser')) }}</span>
            {{ $t('goalChanges.created', { title: goalSettings.featureNameSingular }) }}
          </div>
          <div class="_time">
            {{ timeAgo(goal.createdAt) }}
          </div>
        </div>
      </div>
      <div
        v-if="hasMore && loading"
        class="_spinner"
      >
        <m-spinner size="sm" />
      </div>
      <div
        v-else-if="!hasMore && footnote !== null"
        class="_hint -footnote"
      >
        {{ footnote }}
      </div>
    </template>
    <div
      ref="end"
      class="_end"
    />
  </div>
</template>

<script setup>
import GoalChangeItem from '@/components/goal/GoalChangeItem.vue';
import UserAvatar from 'shared/components/UserAvatar.vue';
import useGoalChangeDataSync from '@/composables/goal-change/goal-change-data-sync';
import useGoalChangeRepo, {
  GOAL_CHANGE_HISTORY_DAYS,
  GOAL_CHANGE_RECORDING_START,
  goalChangeWindowStart,
} from '@/composables/goal-change/goal-change-repo';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useUsers from '@/composables/user/users';
import useVisibility from '@/composables/visibility';
import { DateTime } from 'luxon';
import { changeFieldLabel, joinFieldLabels } from '@/lib/goal-change/format';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { displayName } from 'shared/composables/user';
import { goalChangeField } from 'shared/constants.json';
import { goalChangeItems, groupGoalChanges, groupWindowStart } from '@/lib/goal-change/group';
import { humanReadableDiffFromNow } from 'shared/lib/time';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';
import { withMetrics } from '@/lib/goal-change/metric';

const props = defineProps({ goal: { type: Object, required: true } });

const { t } = useI18n();
const { userLang } = useLoggedInUser();
const userSvc = useUsers();
const goalChangeRepo = useGoalChangeRepo();
useGoalChangeDataSync(computed(() => props.goal.uid));
const { goalSettings } = useGoalSettings();

const loading = ref(false);
const failed = ref(false);
const hasMore = ref(false);
let request = 0;

const time = (change) => new Date(change.changedAt).getTime();

const goalChanges = computed(() => goalChangeRepo.changesOf(props.goal.uid).sort((a, b) => time(b) - time(a)));

const groups = computed(() => groupGoalChanges(withMetrics(goalChanges.value, props.goal.metric))
  .map((group) => ({ ...group, items: goalChangeItems(group.changes) }))
  .filter((group) => group.items.length > 0));

const shownUids = () => goalChanges.value.map((c) => c.uid);

// A page can end inside a history entry, which chains changes up to the grouping window apart, so keep loading the
// window before the oldest change until a gap or another actor ends the entry.
const completeLastEntry = (current) => {
  if (!hasMore.value || goalChanges.value.length === 0) {
    return Promise.resolve();
  }
  const oldest = goalChanges.value.at(-1);
  return goalChangeRepo.getList({ goalId: props.goal.uid, excludedIds: shownUids(), since: groupWindowStart(oldest.changedAt) })
    .then((res) => {
      if (current !== request) {
        return undefined;
      }
      const older = res.goalChanges.filter((c) => time(c) <= time(oldest));
      if (older.length === 0 || older.some((c) => c.actor?.uid !== oldest.actor?.uid)) {
        return undefined;
      }
      return completeLastEntry(current);
    });
};

// Always the newest page without the shown changes: an offset or changedAt cursor would skip changes created since,
// and one save can stamp more than a page of changes with the same changedAt.
const load = () => {
  request += 1;
  const current = request;
  loading.value = true;
  return goalChangeRepo.getList({ goalId: props.goal.uid, excludedIds: shownUids() })
    .then((res) => {
      if (current !== request) {
        return undefined;
      }
      hasMore.value = res.goalChanges.length > 0 && res.count > res.goalChanges.length;
      return completeLastEntry(current);
    })
    .catch(logCatch(() => {
      if (current === request) {
        failed.value = true;
      }
    }))
    .finally(() => {
      if (current === request) {
        loading.value = false;
      }
    });
};

const loadMore = () => {
  if (!hasMore.value || loading.value || failed.value) {
    return;
  }
  load();
};

// Watched rather than triggered on entering the viewport: a page of short entries may not fill the panel, leaving the
// end visible with nothing to scroll.
const end = ref(null);
const { isVisible: endVisible } = useVisibility(end, { rootMargin: '0px', unmountDelayMs: 0 });
watch([endVisible, loading], () => {
  if (endVisible.value) {
    loadMore();
  }
});

const open = () => {
  hasMore.value = false;
  failed.value = false;
  load();
};

// Nothing syncs a goal's changes once the panel lets go of it, so they would be outdated by the next visit.
const release = (goalId) => goalChangeRepo.removeLocally(goalChangeRepo.changesOf(goalId).map((c) => c.uid));

onMounted(open);
watch(() => props.goal.uid, (_, previous) => {
  release(previous);
  open();
});
onBeforeUnmount(() => release(props.goal.uid));

const windowStart = goalChangeWindowStart();
const recordingStartedInWindow = GOAL_CHANGE_RECORDING_START > windowStart;
const recordingStart = DateTime.fromJSDate(GOAL_CHANGE_RECORDING_START).toLocaleString(DateTime.DATE_MED);

const createdAt = computed(() => new Date(props.goal.createdAt));
const createdInWindow = computed(() => createdAt.value >= windowStart);
const userOf = (user) => (user ? userSvc.selectSingle(user.uid) ?? null : null);
const creator = computed(() => userOf(props.goal.creator));

const isEmpty = computed(() => groups.value.length === 0 && !hasMore.value && !createdInWindow.value);
const emptyText = computed(() => (recordingStartedInWindow
  ? t('goalChanges.emptySince', { date: recordingStart })
  : t('goalChanges.empty', { days: GOAL_CHANGE_HISTORY_DAYS })));

// Goals created in the window after recording started have their complete history; older ones explain the gap.
const footnote = computed(() => {
  if (createdInWindow.value && createdAt.value >= GOAL_CHANGE_RECORDING_START) {
    return null;
  }
  if (recordingStartedInWindow) {
    return t('goalChanges.recordedSince', { date: recordingStart });
  }
  return t('goalChanges.footnote', { days: GOAL_CHANGE_HISTORY_DAYS });
});

const actor = (group) => userOf(group.actor);

const timeAgo = (timestamp) => {
  const res = humanReadableDiffFromNow(DateTime.fromISO(timestamp));
  return t(res.translationKey, res);
};

const actorName = (group) => displayName(actor(group), t('goalChanges.unknownUser'));

const summary = (group) => {
  const changes = group.items.map((item) => item[0]);
  if (changes.length === 1 && changes[0].field === goalChangeField.description) {
    return t('goalChanges.editedDescription');
  }
  const labels = changes.map((c) => changeFieldLabel(c, { t, language: userLang.value, goalName: goalSettings.value.featureNameSingular }));
  return t('goalChanges.changed', { fields: joinFieldLabels([...new Set(labels)], userLang.value) });
};
</script>

<style scoped lang="scss" type="text/scss">
  .goal-change-list {
    ._spinner {
      display: flex;
      justify-content: center;
      padding: 1.2rem;
    }

    ._hint {
      margin: .8rem 0;
      font-size: $font-size-2;
      color: $font-color-secondary;

      &.-footnote {
        padding-left: 3rem;
        margin-top: 1.6rem;
      }
    }

    ._end {
      height: .1rem;
    }

    ._group {
      display: grid;
      grid-template-columns: 2rem 1fr;
      gap: 1rem;
      padding: 1.6rem 0;
      border-bottom: 1px solid $border-color;
    }

    ._avatar {
      margin-top: .1rem;
    }

    ._content {
      min-width: 0;
    }

    ._headline {
      overflow-wrap: anywhere;
    }

    ._name {
      font-weight: $font-weight-semibold;
    }

    ._time {
      margin-top: .2rem;
      font-size: $font-size-2;
      color: $font-color-secondary;
    }

    ._changes {
      margin-top: .6rem;
    }
  }
</style>
